import { useFormContext } from "react-hook-form";
import { Grid } from "@material-ui/core";
import { IFixedDiscountRule, IHeadquarters, IStates } from "@refund/register-fixed-discount-rules/register/contracts"
import { AutocompleteField } from "./AutocompleteField";
import { useQueryClient } from "react-query";
import { useHeadquarters, useDebounce } from "../hooks";
import { ApiLoadingSpinner, ApiUpdatingSpinner } from "@funcional/react-ui";
import { useRef, useState } from "react";
import { IRefundRule } from "@refund/query-fixed-discount-rules/contracts";

const containerSpacing = 3;

interface IHeadquartersCustomFields {
  label: string | React.ReactNode;
  disabled: boolean;
  errorMessage: string;
  required: boolean;
}

const getDefaultHeadquartersInEditMode = (editMode, defaults, headquarters): IHeadquarters[] => {
  if (editMode) {
    if (defaults && defaults.headquartersObj && defaults.headquartersObj.length > 0) {
      if (headquarters && headquarters instanceof Array && headquarters.length > 0) {
        const headquartersIds = headquarters?.map(headquarter => headquarter.id) || []
        const defaultsHeadquarters = defaults.headquartersObj.filter(defaultHeaquarter =>
          !headquartersIds.includes(defaultHeaquarter.id)
        )
        headquarters = [...headquarters, ...defaultsHeadquarters.map(defaultHeadquater => ({
          id: defaultHeadquater.id,
          name: defaultHeadquater.name,
        }))]
      } else {
        headquarters = [...defaults.headquartersObj]
      }
    }
  }
  return headquarters
}
const getDefaultStatesInEditMode = (editMode, defaults, states): IStates[] => {
  if (editMode) {
    if (defaults && defaults.statesObj && defaults.statesObj.length > 0) {
      if (states && states instanceof Array && states.length > 0) {
        const statesIds = states?.map(state => state.stateId) || []
        const defaultsStates = defaults.statesObj.filter(defaultState =>
          !statesIds.includes(defaultState.stateId)
        )
        states = [...states, ...defaultsStates.map(defaultState => ({
          stateId: defaultState.stateId,
          name: defaultState.name,
        }))]
      } else {
        states = [...defaults.statesObj]
      }
    }
  }
  return states
}
export function ChainsAndStatesForm({
  defaults,
  editMode
}: {
  defaults?: IRefundRule,
  editMode?: boolean,
}) {

  const {
    control,
    formState: { errors },
    getValues,
    setValue
  } = useFormContext() as any;

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 2000);

  let states = Array();
  const { data: statesData, status: statesStatus, isFetching: statesIsFetching } = useQueryClient().getQueryState('refund_states') as any;
  states = statesData;
  states = getDefaultStatesInEditMode(editMode, defaults, states)

  const statesOptionsFieldValue = states?.map(state => {
    return {
      label: state.name,
      value: state.name,
    }
  }) || [];

  const statesDefaultFieldValue = getValues("states")?.map((state) => {
    return {
      label: state,
      value: state,
    }
  }) || [];

  const saveStatesId = (data) => {
    const selectedStatesId = statesData?.filter(state => data.includes(state.name)).map(state => state.stateId)
    setValue('statesId', selectedStatesId)
  }
  const statesOnChange = () => {
    const selectedStates = getValues('states');
    saveStatesId(selectedStates)
  }

  let headquarters: IHeadquarters[] = [];
  const { status: headquartersStatus, data: headquartersData, isFetching: headquartersIsFetching, error: headquartersErrors } = useHeadquarters(debouncedSearch);

  const headquartersQueryError: any = headquartersErrors
  headquarters = headquartersData !== undefined ? headquartersData?.edges?.map(e => e.node) : [];
  headquarters = getDefaultHeadquartersInEditMode(editMode, defaults, headquarters)

  const headquartersOptionsFieldValue = headquarters?.map(headquarter => {
    return {
      label: headquarter.name,
      value: headquarter.name,
    }
  }) || [];

  const headquartersDefaultFieldValue = getValues("headquarters")?.map((headquarter) => {
    return {
      label: headquarter,
      value: headquarter,
    }
  }) || [];

  const headquartersTextFieldOnChange = (event) => {
    setSearchTerm(event.target.value)
  }
  const saveHeadquartersId = (data) => {
    const selectedHeadquartersId = headquarters?.filter(headquarter => data.includes(headquarter.name)).map(headquarter => headquarter.id)
    console.log('headquartersId', selectedHeadquartersId);
    setValue('headquartersId', selectedHeadquartersId)
  }
  const headquartersSelectBoxOnChange = () => {
    const selectedHeadquarters = getValues('headquarters');
    saveHeadquartersId(selectedHeadquarters)
  }
  const getHeadquartersFields = (): IHeadquartersCustomFields => {
    let label: string | React.ReactNode = "Digite para buscar a(s) rede(s) desejada(s)";
    let required = true;
    let disabled = false;
    let errorMessage = "";
    if (searchTerm) {
      if (headquartersStatus === "loading") {
        label = <div className="relative text-clip -top-3"><ApiLoadingSpinner /></div>;
      } else if (headquartersStatus === "error" && headquartersQueryError && (typeof headquartersQueryError === "object") && Object.keys(headquartersQueryError).includes('message') && headquartersQueryError.message.indexOf('NÃO FORAM ENCONTRADAS MATRIZES PARA ESSE NOME') === 0) {
        errorMessage = "Ocorreu um erro ao buscar os dados das redes, tente realizar a busca novamente. Caso o problema persista, entre em contato com o administrador do sistema.";
      } else if (headquartersIsFetching) {
        label = <div className="relative text-clip -top-3"><ApiUpdatingSpinner /></div>
      } else if (!headquartersData?.edges || headquartersData.edges.length === 0) {
        errorMessage = "Não foi possível encontrar nenhuma rede com os caracteres digitados acima.";
      }
    }
    return {
      label,
      errorMessage,
      disabled,
      required
    };
  }

  const getStatesFields = (): IHeadquartersCustomFields => {
    let label: string | React.ReactNode = "Estados";
    let required = false;
    let disabled = true;
    let errorMessage = "";

    if (Object.keys(headquartersDefaultFieldValue).length > 0) {
      label = "Selecione o(s) Estado(s)"
      required = true;
      disabled = false;
    } else {
      if (statesStatus === "loading" || statesStatus === "idle") {
        label = <div className="relative text-clip -top-3"><ApiLoadingSpinner /></div>;
      } else if (statesStatus === "error") {
        label = "Ocorreu um erro ao buscar os estados.";
        errorMessage = "Ocorreu um erro ao buscar os dados dos estados, saia da tela de cadastro e entre novamente para realizar uma nova busca. Caso o problema persista, entre em contato com o administrador do sistema.";
      } else if (statesIsFetching) {
        label = <div className="relative text-clip -top-3"><ApiUpdatingSpinner /></div>
      } else if (statesOptionsFieldValue.length === 0) {
        label = "Não foi possível obter os dados dos estados.";
        errorMessage = "Não foi possível obter os dados dos estados, saia da tela de cadastro e entre novamente para realizar uma nova busca. Caso o problema persista, entre em contato com o administrador do sistema.";
      } else {
        label = "Selecione o(s) Estado(s)"
        required = true;
        disabled = false;
      }
    }
    return {
      label,
      errorMessage,
      disabled,
      required
    };
  }

  return (
    <Grid container spacing={containerSpacing} className="max-w-xl">
      <Grid item xs={12}>
        <AutocompleteField
          freeSolo
          disableCloseOnSelect
          helperText="Selecione a(s) rede(s) ou grupo(s) de rede(s)"
          name="headquarters"
          control={control}
          options={headquartersOptionsFieldValue as any}
          defaultValue={headquartersDefaultFieldValue as any}
          selectBoxOnChange={headquartersSelectBoxOnChange}
          textFieldOnChange={headquartersTextFieldOnChange}
          {...getHeadquartersFields()}
        />
      </Grid>

      <Grid item xs={12}>
        <AutocompleteField
          freeSolo
          disableCloseOnSelect
          helperText="Selecione o(s) estado(s)"
          name="states"
          control={control}
          options={statesOptionsFieldValue as any}
          defaultValue={statesDefaultFieldValue as any}
          selectBoxOnChange={statesOnChange}
          {...getStatesFields()}
        />
      </Grid>
    </Grid>
  );
}
